import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import messages from "../../../../assets/locale/messages";
import { eventStatuses } from "../../../../utils/Constants";
import { isPortalEventCancelled } from "../../../../utils/companyPortal/eventEditRules";
import EventTimesheet from "./EventTimesheet";
import {
  TIMESHEET_SLOT_TABS,
  TIMESHEET_URL_PARAMS,
} from "./constants";
import { hasActiveTimesheetFilters } from "./timesheetListHelpers";
import { getRoleOptionsFromEvent } from "../Applicants/eventRequestMappers";
import { getTimesheetApplicantName, normalizeTimesheetApplicantId } from "./timesheetUtils";
import {
  AddSlotModal,
  DeleteSlotModal,
  hasEventTrainingStarted,
} from "./modals";

import "./EventTimesheet.scss";

const SLOT_MODAL_MODES = {
  add: "add",
  edit: "edit",
};

const CLOSED_SLOT_MODAL = {
  open: false,
  mode: SLOT_MODAL_MODES.add,
  slot: null,
};

const CLOSED_DELETE_SLOT_MODAL = {
  open: false,
  slot: null,
};

const STARTED_EVENT_STATUSES = [
  eventStatuses.ongoing,
  eventStatuses.past,
];

const EventTimesheetTab = ({
  event,
  className = "event-timesheet-tab",
  integration,
  selectedApplicantId: selectedApplicantIdProp,
  onApplicantSelect,
}) => {
  const history = useHistory();
  const location = useLocation();
  const isApplicantControlled = onApplicantSelect !== undefined;
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared, timeSheet } = messages[lang];
  const copy = events.viewEvents.timesheetTab;

  const [activeSlotTab, setActiveSlotTab] = useState(TIMESHEET_SLOT_TABS.shifts);
  const [isAddSlotSaving, setIsAddSlotSaving] = useState(false);
  const [slotModal, setSlotModal] = useState(CLOSED_SLOT_MODAL);
  const [deleteSlotModal, setDeleteSlotModal] = useState(CLOSED_DELETE_SLOT_MODAL);

  const {
    search,
    filters,
    statusOptions,
    selectedApplicant: integratedSelectedApplicant,
    applicants: integratedApplicants,
    listPagination,
    shiftsPagination,
    trainingsRows,
    showExportAll = true,
    canAddSlot: canAddSlotPermission,
    canEditSlot = true,
    canDeleteSlot = true,
    onSearchChange,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onShiftsPageChange,
    onShiftsPageSizeChange,
    onSlotSave,
    onSlotDelete,
    onExportAll,
    setSelectedApplicantId,
  } = integration;

  const applicantIdFromUrl = useMemo(
    () =>
      normalizeTimesheetApplicantId(
        new URLSearchParams(location.search).get(TIMESHEET_URL_PARAMS.applicant),
      ),
    [location.search],
  );

  const applicantId = isApplicantControlled
    ? normalizeTimesheetApplicantId(selectedApplicantIdProp)
    : applicantIdFromUrl;

  const selectedApplicant = useMemo(() => {
    if (!applicantId) {
      return null;
    }

    if (
      integratedSelectedApplicant &&
      normalizeTimesheetApplicantId(integratedSelectedApplicant.id) ===
        applicantId
    ) {
      return integratedSelectedApplicant;
    }

    return (
      integratedApplicants.find(
        (row) => normalizeTimesheetApplicantId(row.id) === applicantId,
      ) ?? null
    );
  }, [applicantId, integratedApplicants, integratedSelectedApplicant]);

  useEffect(() => {
    setSelectedApplicantId?.(applicantId);
  }, [applicantId, setSelectedApplicantId]);

  useEffect(() => {
    if (!applicantId || selectedApplicant) {
      return;
    }

    if (isApplicantControlled) {
      onApplicantSelect(null);
      return;
    }

    const params = new URLSearchParams(location.search);
    params.delete(TIMESHEET_URL_PARAMS.applicant);
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [
    applicantId,
    history,
    isApplicantControlled,
    location.pathname,
    location.search,
    onApplicantSelect,
    selectedApplicant,
  ]);

  useEffect(() => {
    if (!selectedApplicant?.id) {
      return;
    }

    setActiveSlotTab(TIMESHEET_SLOT_TABS.shifts);
    onShiftsPageChange(1);
  }, [onShiftsPageChange, selectedApplicant?.id]);

  const isEventStarted = STARTED_EVENT_STATUSES.includes(event?.status);
  const canAddTrainingSlot =
    isEventStarted || hasEventTrainingStarted(event);
  // A cancelled event's working logs stay as they are (PROLINE-4124).
  const isEventCancelled = isPortalEventCancelled(event?.status);
  const canEditSlots = canEditSlot !== false && !isEventCancelled;
  const canDeleteSlots = canDeleteSlot !== false && !isEventCancelled;

  const roleOptions = useMemo(
    () => getRoleOptionsFromEvent(event, lang),
    [event, lang],
  );

  const shiftsRowActions = useMemo(() => {
    const actions = [];

    if (canEditSlots) {
      actions.push({ key: "edit", label: shared.edit });
    }

    if (canDeleteSlots) {
      actions.push({
        key: "delete",
        label: shared.delete,
        danger: true,
      });
    }

    return actions;
  }, [canDeleteSlots, canEditSlots, shared.delete, shared.edit]);

  const trainingsRowActions = useMemo(() => {
    const actions = [];

    if (canEditSlots) {
      actions.push({ key: "edit", label: shared.edit });
    }

    if (canDeleteSlots) {
      actions.push({
        key: "delete",
        label: shared.delete,
        danger: true,
      });
    }

    return actions;
  }, [canDeleteSlots, canEditSlots, shared.delete, shared.edit]);

  const applicantsLabels = {
    rangeTemplate: copy.rangeTemplate,
    rowsPerPageLabel: copy.rowsPerPageLabel,
  };

  const handleRowClick = ({ row }) => {
    const rowApplicantId = normalizeTimesheetApplicantId(row.id);
    if (!rowApplicantId) {
      return;
    }

    setSelectedApplicantId?.(rowApplicantId);

    if (isApplicantControlled) {
      onApplicantSelect(rowApplicantId);
      return;
    }

    const params = new URLSearchParams(location.search);
    params.set(TIMESHEET_URL_PARAMS.applicant, rowApplicantId);
    history.push({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  const applicantName = useMemo(
    () =>
      selectedApplicant
        ? getTimesheetApplicantName(selectedApplicant, lang)
        : "",
    [lang, selectedApplicant],
  );

  const handleSlotRowAction = (actionKey, row) => {
    if (actionKey === "edit") {
      setSlotModal({
        open: true,
        mode: SLOT_MODAL_MODES.edit,
        slot: row,
      });
      return;
    }

    if (actionKey === "delete") {
      setDeleteSlotModal({
        open: true,
        slot: row,
      });
    }
  };

  const handleDeleteSlotConfirm = (slot) => {
    if (!slot?.id) {
      return;
    }

    onSlotDelete(slot, activeSlotTab);
    setDeleteSlotModal(CLOSED_DELETE_SLOT_MODAL);
  };

  const handleSlotSave = (formValues, { mode, slot } = {}) => {
    setIsAddSlotSaving(true);

    const started = onSlotSave(formValues, {
      mode,
      slot,
      slotType: activeSlotTab,
      onSuccess: () => {
        setSlotModal(CLOSED_SLOT_MODAL);
        setIsAddSlotSaving(false);
      },
      onError: () => {
        setIsAddSlotSaving(false);
      },
    });

    if (!started) {
      setIsAddSlotSaving(false);
    }
  };

  const listLoading = false;
  const shiftsLoading = false;
  const trainingsLoading = false;
  const exportDisabled = !isEventStarted || !listPagination.total;
  const canAddSlotByTab =
    activeSlotTab === TIMESHEET_SLOT_TABS.trainings
      ? canAddTrainingSlot
      : isEventStarted;
  const canAddSlot =
    (canAddSlotPermission ?? true) && canAddSlotByTab && !isEventCancelled;

  return (
    <div className={className}>
      <EventTimesheet
        selectedApplicant={selectedApplicant}
        listProps={{
          copy,
          applicantsLabels,
          lang,
          rows: listPagination.rows,
          isLoading: listLoading,
          isEventStarted,
          search,
          filters,
          roleOptions,
          statusOptions,
          page: listPagination.page,
          pageSize: listPagination.pageSize ?? 10,
          total: listPagination.total,
          pages: listPagination.pages,
          rangeFrom: listPagination.rangeFrom,
          rangeTo: listPagination.rangeTo,
          onSearchChange,
          onFilterChange,
          onExportAll: onExportAll ?? (() => {}),
          exportDisabled,
          showExportAll,
          onRowClick: handleRowClick,
          onPageChange,
          onPageSizeChange,
          hasActiveSearchOrFilter: hasActiveTimesheetFilters(search, filters),
        }}
        applicantProps={{
          copy,
          timeSheetCopy: timeSheet,
          sharedLabels: shared,
          applicantsLabels,
          lang,
          activeTab: activeSlotTab,
          onTabChange: setActiveSlotTab,
          canAddSlot,
          onAddSlot: () =>
            setSlotModal({
              open: true,
              mode: SLOT_MODAL_MODES.add,
              slot: null,
            }),
          shiftsRows: shiftsPagination.rows,
          shiftsLoading,
          shiftsPage: shiftsPagination.page,
          shiftsPageSize: shiftsPagination.pageSize ?? 10,
          shiftsTotal: shiftsPagination.total,
          shiftsPages: shiftsPagination.pages,
          shiftsRangeFrom: shiftsPagination.rangeFrom,
          shiftsRangeTo: shiftsPagination.rangeTo,
          shiftsRowActions,
          onShiftRowAction: handleSlotRowAction,
          onShiftsPageChange,
          onShiftsPageSizeChange,
          trainingsRows,
          trainingsLoading,
          trainingsRowActions,
          onTrainingRowAction: handleSlotRowAction,
        }}
      />
      <AddSlotModal
        open={slotModal.open}
        mode={slotModal.mode}
        slot={slotModal.slot}
        onClose={() => setSlotModal(CLOSED_SLOT_MODAL)}
        applicantName={applicantName}
        applicant={selectedApplicant}
        slotType={activeSlotTab}
        event={event}
        loading={isAddSlotSaving}
        onSave={handleSlotSave}
      />
      <DeleteSlotModal
        open={deleteSlotModal.open}
        slot={deleteSlotModal.slot}
        slotType={activeSlotTab}
        loading={false}
        onClose={() => setDeleteSlotModal(CLOSED_DELETE_SLOT_MODAL)}
        onConfirm={handleDeleteSlotConfirm}
      />
    </div>
  );
};

EventTimesheetTab.propTypes = {
  event: PropTypes.object,
  className: PropTypes.string,
  integration: PropTypes.shape({
    search: PropTypes.string,
    filters: PropTypes.object,
    statusOptions: PropTypes.array,
    selectedApplicant: PropTypes.object,
    applicants: PropTypes.array,
    listPagination: PropTypes.object.isRequired,
    shiftsPagination: PropTypes.object.isRequired,
    trainingsRows: PropTypes.array,
    onSearchChange: PropTypes.func.isRequired,
    onFilterChange: PropTypes.func.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onPageSizeChange: PropTypes.func.isRequired,
    onShiftsPageChange: PropTypes.func.isRequired,
    onShiftsPageSizeChange: PropTypes.func.isRequired,
    onSlotSave: PropTypes.func.isRequired,
    onSlotDelete: PropTypes.func.isRequired,
    onExportAll: PropTypes.func,
    showExportAll: PropTypes.bool,
    canAddSlot: PropTypes.bool,
    canEditSlot: PropTypes.bool,
    canDeleteSlot: PropTypes.bool,
    setSelectedApplicantId: PropTypes.func,
  }).isRequired,
  selectedApplicantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onApplicantSelect: PropTypes.func,
};

EventTimesheetTab.defaultProps = {
  event: null,
  className: "event-timesheet-tab",
  selectedApplicantId: undefined,
  onApplicantSelect: undefined,
};

export default EventTimesheetTab;
